"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getLegTransferAdjustments = void 0;
const _ = require("lodash");
const dayjs = require("dayjs");
const orders_1 = require("../orders");
/**
 * Processes a variant and determines transfer status based on various conditions
 * @param variant The variant to process
 * @returns Object containing variant information and transfer status
 */
const getLegTransferAdjustments = (variant) => {
    // Type guard to ensure variant has required properties
    if (!variant?.config?.legs || !variant.id) {
        return {
            variant_id: variant?.id || "unknown",
            type: "ERROR",
            transfer_to_delete: null,
            hotel_order_line_id: null,
            from_area: null,
            via_area: null,
            error: "Invalid variant structure",
            region_id: null,
        };
    }
    // Get the last leg from the variant configuration
    const prev_leg = _.last(variant.config.legs);
    // Early return if prev_leg is undefined
    if (!prev_leg) {
        return {
            variant_id: variant.id,
            type: "ERROR",
            transfer_to_delete: null,
            hotel_order_line_id: null,
            from_area: null,
            via_area: null,
            error: "No legs found in variant",
            region_id: null,
        };
    }
    // Get start and end dates
    const start_date = prev_leg.trip?.start_date;
    // Early return if start_date is undefined
    if (!start_date) {
        return {
            variant_id: variant.id,
            type: "ERROR",
            transfer_to_delete: null,
            hotel_order_line_id: null,
            from_area: null,
            via_area: null,
            error: "No start date found",
            region_id: null,
        };
    }
    const end_date = dayjs(start_date)
        .add(prev_leg.trip?.duration_days_nights || 0, "day")
        .format("YYYY-MM-DD");
    // Check if there's golf scheduled on the last day
    const has_golf_on_last_day = Array.isArray(prev_leg.golf) &&
        prev_leg.golf.length > 0 &&
        Boolean(prev_leg.golf[prev_leg.golf.length - 1]);
    const last_day_golf = variant.order_lines?.filter((orderLine) => orderLine.type === "GOLF" && orderLine.start_date === end_date) || [];
    // Filter for transfers on the last day
    const last_day_transfers = variant.order_lines?.filter((orderLine) => orderLine.type === "TRANSFER" && orderLine.start_date === end_date) || [];
    // Filter for hotel order lines
    const hotel_lines = variant.order_lines?.filter((orderLine) => orderLine.type === "HOTEL" && orderLine.start_date === start_date) || [];
    // Check if there is a return golf transfer
    const has_return_golf_transfer = last_day_transfers.some((transfer) => {
        if (!transfer.versions)
            return false;
        const latest_version = (0, orders_1.getLatestOrderLineVersion)(transfer.versions);
        const transfer_config = latest_version?.config;
        return transfer_config?.where_when_who?.type === "RETURN";
    });
    // Check if there is a hotel
    const has_hotel = hotel_lines.length > 0;
    let hotel_order_line_id = null;
    // Get from_area details from the hotel if available
    let from_area = null;
    let hotel_region_id = null;
    if (has_hotel && hotel_lines.length > 0) {
        const hotel = hotel_lines[0];
        hotel_order_line_id = hotel.id;
        if (!hotel.versions)
            return {
                variant_id: variant.id,
                type: "ERROR",
                transfer_to_delete: null,
                hotel_order_line_id: null,
                from_area: null,
                via_area: null,
                error: "No versions found for hotel",
                region_id: null,
            };
        const latest_version = (0, orders_1.getLatestOrderLineVersion)(hotel.versions);
        const hotel_config = latest_version?.config;
        if (hotel_config?.where_when_who?.hotel) {
            from_area =
                hotel_config.where_when_who.hotel.id &&
                    hotel_config.where_when_who.hotel.name?.en
                    ? {
                        id: hotel_config.where_when_who.hotel.id,
                        name: hotel_config.where_when_who.hotel.name.en,
                        type: "Hotel",
                    }
                    : null;
            hotel_region_id = hotel_config.where_when_who.region_id;
        }
    }
    else {
        from_area =
            prev_leg?.transfer?.primary_pickup_area?.id &&
                prev_leg?.transfer?.primary_pickup_area?.name?.en
                ? {
                    id: prev_leg.transfer.primary_pickup_area.id,
                    name: prev_leg.transfer.primary_pickup_area.name.en,
                    type: "Area",
                }
                : null;
    }
    // Decision logic for different scenarios
    // Case 1: No transfers on the last day
    if (last_day_transfers.length === 0) {
        return {
            variant_id: variant.id,
            type: "NO_TRANSFER",
            transfer_to_delete: null,
            hotel_order_line_id: null,
            from_area,
            via_area: null,
            region_id: hotel_region_id || null,
        };
    }
    // Case 2: No golf on the last day but there is a transfer
    if (!has_golf_on_last_day && last_day_transfers.length === 1) {
        return {
            variant_id: variant.id,
            type: "NO_GOLF_STANDARD_ONE_WAY",
            hotel_order_line_id: hotel_order_line_id,
            transfer_to_delete: last_day_transfers[0],
            from_area,
            via_area: null,
            region_id: hotel_region_id || null,
        };
    }
    // Case 3: Has golf on the last day with a return golf transfer
    if (has_golf_on_last_day &&
        last_day_transfers.length === 1 &&
        has_return_golf_transfer) {
        return {
            variant_id: variant.id,
            type: "GOLF_ONLY_NO_TRANSFER",
            hotel_order_line_id: hotel_order_line_id,
            transfer_to_delete: null,
            from_area,
            via_area: null,
            region_id: hotel_region_id || null,
        };
    }
    // Case 4: Has golf on the last day with a custom departure transfer
    if (has_golf_on_last_day &&
        last_day_transfers.length > 0 &&
        prev_leg.transfer?.departure_transfer_type === "CUSTOM") {
        const custom_transfer = last_day_transfers.find((transfer) => {
            if (!transfer.versions)
                return false;
            const latest_version = (0, orders_1.getLatestOrderLineVersion)(transfer.versions);
            const transfer_config = latest_version?.config;
            return transfer_config?.where_when_who?.type === "CUSTOM";
        });
        if (last_day_golf.length > 0) {
            const golf_line = last_day_golf[0];
            if (!golf_line.versions)
                return {
                    variant_id: variant.id,
                    type: "ERROR",
                    transfer_to_delete: null,
                    hotel_order_line_id: null,
                    from_area: null,
                    via_area: null,
                    error: "No versions found for golf",
                    region_id: null,
                };
            const golf_config = (0, orders_1.getLatestOrderLineVersion)(golf_line.versions)
                ?.config;
            return {
                variant_id: variant.id,
                type: "GOLF_CUSTOM",
                transfer_to_delete: custom_transfer || null,
                from_area,
                hotel_order_line_id: hotel_order_line_id,
                via_area: {
                    id: golf_config?.course?.id,
                    name: golf_config?.course?.name?.en,
                    type: "Course",
                },
                region_id: custom_transfer?.region_id || null,
            };
        }
        return {
            variant_id: variant.id,
            type: "GOLF_CUSTOM",
            transfer_to_delete: custom_transfer || null,
            hotel_order_line_id: hotel_order_line_id,
            from_area,
            via_area: null,
            region_id: custom_transfer?.region_id || null,
        };
    }
    // Case 5: Has golf on the last day with a standard departure transfer
    if (has_golf_on_last_day &&
        last_day_transfers.length > 0 &&
        prev_leg.transfer?.departure_transfer_type === "STANDARD") {
        const standard_transfer = last_day_transfers.find((transfer) => {
            if (!transfer.versions)
                return false;
            const latest_version = (0, orders_1.getLatestOrderLineVersion)(transfer.versions);
            const transfer_config = latest_version?.config;
            return transfer_config?.where_when_who?.type === "ONE_WAY";
        });
        return {
            variant_id: variant.id,
            type: "GOLF_STANDARD_ONE_WAY",
            transfer_to_delete: standard_transfer || null,
            hotel_order_line_id: hotel_order_line_id,
            from_area,
            via_area: null,
            region_id: standard_transfer?.region_id || null,
        };
    }
    // Default case when no other conditions are met
    return {
        variant_id: variant.id,
        type: "NO_TRANSFER",
        transfer_to_delete: null,
        hotel_order_line_id: null,
        from_area,
        via_area: null,
        region_id: null,
    };
};
exports.getLegTransferAdjustments = getLegTransferAdjustments;
